<template>
  <!-- Only rendered once a target node exists. Teleporting to a missing selector
       makes Vue warn and drop the content silently. -->
  <Teleport v-if="target" :to="target">
    <slot />
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

import { TITAN_REMOTE_NAV_TAG } from "@/remote/contract";

/**
 * Teleports a layout's existing side nav into the host's `<titan-remote-nav>`
 * outlet (spec item 8).
 *
 * WHY A PORTAL rather than a self-contained nav component: each layout's
 * `navMenuSetUp` enriches the shared `usePrimaryNavItems` items before rendering
 * them — filtering by enabled solutions and licences (`isNavItemDisabled`,
 * `mspGlobalOrganisationId`) and swapping a test id on the CONNECTIONS_V2 flag. A
 * nav rebuilt from `getNavItems()` alone would show entries for products the
 * customer has no licence for. Teleporting the layout's own, already-correct nav
 * keeps one source of truth and leaves that logic untouched.
 *
 * This is also what the shell expects: its comment describes `<red-remote-nav>`
 * as "the portal OUTLET alone", which is why it must never be wrapped in a
 * `v-if` — that would unmount and remount the outlet on every route change.
 */

const target = ref<Element | null>(null);

/**
 * The host may render the outlet before or after the remote boots, so a single
 * lookup on mount is not enough. Watch for it, and stop as soon as it appears.
 */
let observer: MutationObserver | null = null;

const findTarget = () => document.querySelector(TITAN_REMOTE_NAV_TAG);

onMounted(() => {
  target.value = findTarget();
  if (target.value) return;

  observer = new MutationObserver(() => {
    const found = findTarget();
    if (!found) return;

    target.value = found;
    observer?.disconnect();
    observer = null;
  });

  observer.observe(document.body, { childList: true, subtree: true });
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});
</script>
